import React, {
  memo, useCallback, useMemo, useState,
} from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table } from 'reactstrap';
import { ReactComponent as CreateIcon } from 'assets/images/create.svg';

// import images from 'assets/images';
import {
  BOOKING_STATUS, LOCATIONS, LOGIN_TYPE, storageKey,
} from 'constants/index';

import AppBadge from 'components/App/AppBadge';
import Button from 'components/App/Button';
import ColGroup from 'components/ColGroup';
import AppTooltip from 'components/App/Tooltip';
import ModalConfirmBooking from 'components/Modals/ModalConfirmBooking';

import { convertIsoDateToString } from 'utils';
import formatMoney from 'utils/formatMoney';

import styles from './DataTableLocations.module.scss';

// BE strips otaSync for non-intranet admins, so data presence alone is the gate (a stale
// client-side isIntranet read off localStorage LOGIN_TYPE is not, WP-ADM-OTA-SYNC-3). A
// FAILED/PENDING otaSync gets a badge; SYNCED/SKIPPED/absent get none.
const OTA_SYNC_LIST_BADGE = {
  FAILED: { label: 'Chưa đẩy Hotellink', color: 'error' },
  PENDING: { label: 'Đang đẩy Hotellink', color: 'warning' },
};

const HEADS = [
  'Mã đặt phòng',
  'Tên khách hàng',
  'Ngày nhận phòng',
  'Ngày trả phòng',
  'Tên phòng',
  'Ngày đặt phòng',
  'Xác nhận',
  'Trạng thái',
  'Giá phòng',
];

function DataTableBooking(props) {
  const {
    bookings,
    onConfirmSuccess,
    isIntranet: isIntranetProp,
    showConfirmColumn,
    // statusProgressData,
  } = props;

  const heads = useMemo(() => {
    if (!showConfirmColumn) {
      return HEADS.filter((head) => head !== 'Xác nhận');
    }
    return HEADS;
  }, [showConfirmColumn]);

  const isIntranetRedux = useSelector((state) => state.Login?.isIntranet);
  const isIntranet = useMemo(() => {
    if (typeof isIntranetProp === 'boolean') return isIntranetProp;
    const loginType = localStorage.getItem(storageKey.LOGIN_TYPE);
    if (loginType === LOGIN_TYPE.EXTRANET) return false;
    if (loginType === LOGIN_TYPE.INTRANET) return true;
    return isIntranetRedux ?? true;
  }, [isIntranetProp, isIntranetRedux]);

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [localConfirmCodes, setLocalConfirmCodes] = useState({});

  const handleOpenConfirmModal = useCallback((booking) => {
    setSelectedBooking(booking);
    setIsOpenConfirmModal(true);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setSelectedBooking(null);
    setIsOpenConfirmModal(false);
  }, []);

  const handleConfirmSuccess = useCallback(
    (bookingId, newCode, res) => {
      setLocalConfirmCodes((prev) => ({
        ...prev,
        [bookingId]: newCode,
      }));
      onConfirmSuccess?.(bookingId, newCode, res);
    },
    [onConfirmSuccess],
  );

  const ButtonType = useCallback(
    (bookingId) => (
      <div className="d-flex">
        <Link to={`${LOCATIONS.MANAGER_BOOKING}/${bookingId}`}>
          <Button outline classes="Action__Button Action__Button__Link">
            Xem chi tiết
          </Button>
        </Link>
      </div>
    ),
    [],
  );

  return (
    <div
      className={styles.Component}
      style={{ '--booking-table-cols': heads.length }}
    >
      {bookings.length === 0 ? (
        <div className="NoData">Chưa có dữ liệu</div>
      ) : (
        <Table responsive className="Table">
          <ColGroup
            list={Array.from({ length: heads.length }, () => 1)}
            containerWidth={heads.length}
            paddingX={0}
          />
          <thead className="Table__Header">
            <tr className="Table__Header__Row">
              {heads.map((head) => (
                <th key={head} className="Table__Header__Row__Content">
                  {head === 'Xác nhận' ? (
                    <div className={styles.ConfirmHeader}>
                      <span>Xác nhận</span>
                      <AppTooltip
                        placement="top"
                        className="tooltip-confirm-booking"
                        content="Đặt phòng này đã được xác nhận với Bookese. Nhập mã xác nhận này là một cách để quý vị quản lý và theo dõi đặt phòng trên hệ thống đặt phòng nội bộ của nơi lưu trú."
                      >
                        <i className={`bx bx-info-circle ${styles.InfoIcon}`} />
                      </AppTooltip>
                    </div>
                  ) : (
                    head
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="Table__Body">
            {bookings.map((booking) => (
              <tr className="Table__Body__Row" key={booking._id}>
                <td className="Table__Body__Row__Content">
                  {booking.orderId}

                  <div className="Action">{ButtonType(booking._id)}</div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="d-flex align-items-center ">
                    {/* <img
                      src={images.locationImage.default}
                      alt="booking-img"
                      className="Table__Body__Row__Content__Image"
                    /> */}

                    <div className="d-flex flex-column">
                      <span className="mr-5">{booking.userInfo.fullName}</span>

                      {/* <span className={`Table__Body__Row__Content__Badge Table__Body__Row
                  __Content__${USER_RANK[booking.userInfo.rank].color}`}>
                    {USER_RANK[booking.userInfo.rank].label}
                  </span> */}
                    </div>
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="Table__Body__Row__Content__Date">
                    {convertIsoDateToString(booking.startedAt)}
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="Table__Body__Row__Content__Date">
                    {convertIsoDateToString(booking.endedAt)}
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="Table__Body__Row__Content__Room">
                    {/* <img
                      src={images.locationImage.default}
                      alt="booking-img"
                      className="Table__Body__Row__Content__Image"
                    /> */}

                    {booking.rooms.map((room) => (
                      <div key={room._id} className="Content__Room__Item">
                        {room.name}
                      </div>
                    ))}
                  </div>
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="Table__Body__Row__Content__Date">
                    {convertIsoDateToString(booking.createdAt)}
                  </div>
                </td>

                {showConfirmColumn && (
                  <td className="Table__Body__Row__Content">
                    {(() => {
                      const confirmCode = localConfirmCodes[booking._id] !== undefined
                        ? localConfirmCodes[booking._id]
                        : booking.confirmBooking;
                      const displayCode = confirmCode || '-';

                      return isIntranet ? (
                        <div
                          className={styles.ConfirmCellViewOnly}
                          title={confirmCode || undefined}
                        >
                          <CreateIcon width={16} height={16} className={styles.PencilIcon} />
                          <span className={styles.ConfirmCodeText}>
                            {displayCode}
                          </span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.ConfirmCellButton}
                          onClick={() => handleOpenConfirmModal(booking)}
                          title={confirmCode || undefined}
                        >
                          <CreateIcon width={16} height={16} className={styles.PencilIcon} />
                          <span className={styles.ConfirmCodeText}>
                            {displayCode}
                          </span>
                        </button>
                      );
                    })()}
                  </td>
                )}

                <td className="Table__Body__Row__Content">
                  <div
                    className="Table__Body__Row__Content__Status"
                  >
                    {booking.bookingStatus === 'WAITING_FOR_REFUND' && (
                      <div className="d-flex flex-column align-items-start">
                        <span>Đã hủy</span>
                        <span
                          className="text-muted"
                          style={{ fontSize: '11px', marginTop: '2px' }}
                        >
                          Đợi hoàn tiền
                        </span>
                      </div>
                    )}
                    {booking.bookingStatus === 'REFUNDED' && (
                      <div className="d-flex flex-column align-items-start">
                        <span>Đã hủy</span>
                        <span
                          className="text-muted"
                          style={{ fontSize: '11px', marginTop: '2px' }}
                        >
                          Đã hoàn tiền
                        </span>
                      </div>
                    )}
                    {booking.bookingStatus !== 'WAITING_FOR_REFUND' &&
                      booking.bookingStatus !== 'REFUNDED' &&
                      (BOOKING_STATUS[booking.bookingStatus]?.label
                        || booking.bookingStatus
                        || '-')}
                  </div>

                  {OTA_SYNC_LIST_BADGE[booking.otaSync?.status] && (
                    <AppBadge
                      className="mt-1"
                      color={OTA_SYNC_LIST_BADGE[booking.otaSync.status].color}
                    >
                      {OTA_SYNC_LIST_BADGE[booking.otaSync.status].label}
                    </AppBadge>
                  )}
                </td>

                <td className="Table__Body__Row__Content">
                  <div className="Table__Body__Row__Content__Status">
                    {formatMoney.format(
                      booking.totalDiscountedPrice || booking.totalPrice,
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <ModalConfirmBooking
        isOpen={isOpenConfirmModal}
        booking={selectedBooking ? {
          ...selectedBooking,
          confirmBooking: localConfirmCodes[selectedBooking._id] !== undefined
            ? localConfirmCodes[selectedBooking._id]
            : selectedBooking.confirmBooking,
        } : null}
        onClose={handleCloseConfirmModal}
        onSuccess={handleConfirmSuccess}
        isReadOnly={isIntranet}
      />
    </div>
  );
}

DataTableBooking.propTypes = {
  bookings: PropTypes.instanceOf(Array),
  onConfirmSuccess: PropTypes.func,
  isIntranet: PropTypes.bool,
  showConfirmColumn: PropTypes.bool,
};

DataTableBooking.defaultProps = {
  bookings: [],
  onConfirmSuccess: undefined,
  isIntranet: undefined,
  showConfirmColumn: true,
};

export default memo(DataTableBooking);
